import API from '../services/api';

const CLIENT_CACHE_KEY = 'incidentInvestigationClientCache_v1';

function extractInvestigationCandidate(payload) {
  try {
    if (!payload) return null;
    const data = payload && payload.data !== undefined ? payload.data : payload;
    if (!data) return null;

    // Many endpoints return arrays
    let candidate = Array.isArray(data) ? data[0] : data;
    if (!candidate) return null;

    // Common wrappers
    if (candidate && typeof candidate === 'object') {
      if (candidate.success && candidate.investigation) return candidate.investigation;
      if (candidate.investigation) return candidate.investigation;
      if (candidate.data && candidate.data.investigation) return candidate.data.investigation;
      if (candidate.success && candidate.incident) return candidate.incident;
      if (candidate.incident) return candidate.incident;
    }

    // If it's already an investigation/incident-like row with investigation fields, accept it.
    if (candidate && typeof candidate === 'object' && !Array.isArray(candidate)) {
      const hasAny = (k) => candidate[k] != null || candidate[String(k).toLowerCase()] != null;
      const looksLikeInvestigation = [
        'CONTRIBUTING_FACTORS',
        'INVESTIGATION_STATUS',
        'CORRECTIVE_ACTIONS_JSON',
        'IMMEDIATE_ACTIONS',
        // WCB/OHS reporting
        'WCB_REPORT_REQUIRED',
        'OHS_REPORT_REQUIRED'
      ].some(hasAny);
      if (looksLikeInvestigation) return candidate;
    }
  } catch (e) {
    // ignore
  }
  return null;
}

async function fetchInvestigationForIncident(incidentId) {
  if (!incidentId) return null;
  // Allow investigation probes in all environments (local, staging, production) so the
  // UI can re-hydrate investigation-only fields (e.g., CONTRIBUTING_FACTORS JSON).
  // If you need to disable probes for any reason, set localStorage.DISABLE_INVESTIGATION_PROBES=1.
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const disabled = window.localStorage.getItem('DISABLE_INVESTIGATION_PROBES');
      if (String(disabled || '') === '1') {
        console.debug('[investigationHelpers] disabled via localStorage.DISABLE_INVESTIGATION_PROBES');
        return null;
      }
    }
  } catch (e) {
    // ignore
  }
  try {
    // Prefer the canonical incident endpoint because production reliably serves it and
    // it can already return incident data merged with investigation fields.
    try {
      console.debug(`[investigationHelpers] probing canonical /api/incidents/${incidentId}`);
      const canonical = await API.get(`/api/incidents/${incidentId}`).catch(e => { console.debug('[investigationHelpers] canonical probe failed:', e && e.message); return null; });
      console.debug('[investigationHelpers] response from canonical /incidents/:id:', canonical && canonical.data ? canonical.data : canonical);
      // canonical route can return the incident merged with investigation fields
      const extracted = extractInvestigationCandidate(canonical);
      if (extracted) {
        console.debug('[investigationHelpers] returning merged investigation-like fields from canonical incident for', incidentId);
        return extracted;
      }
    } catch (e) {
      console.debug('[investigationHelpers] canonical probe error:', e && e.message);
    }

    // Fallback to the dedicated investigation lookup endpoint when available.
    console.debug(`[investigationHelpers] probing /api/incidents/incident-investigations?incidentId=${incidentId}`);
    const resp = await API.get(`/api/incidents/incident-investigations`, { params: { incidentId } }).catch(e => { console.debug('[investigationHelpers] incident-investigations probe failed:', e && e.message); return null; });
    console.debug('[investigationHelpers] response from /incident-investigations:', resp && resp.data ? resp.data : resp);
    {
      const extracted = extractInvestigationCandidate(resp);
      if (extracted) {
        console.debug('[investigationHelpers] returning investigation from dedicated incident-investigations endpoint for', incidentId);
        return extracted;
      }
    }

    // Final fallback for older backends.
    console.debug(`[investigationHelpers] probing legacy /api/investigations/${incidentId}`);
    const legacy = await API.get(`/api/investigations/${incidentId}`).catch(e => { console.debug('[investigationHelpers] legacy probe failed:', e && e.message); return null; });
    console.debug('[investigationHelpers] response from legacy /investigations:', legacy && legacy.data ? legacy.data : legacy);
    {
      const extracted = extractInvestigationCandidate(legacy);
      if (extracted) {
        console.debug('[investigationHelpers] returning investigation from legacy endpoint for', incidentId);
        return extracted;
      }
    }
  } catch (e) {
    console.debug('fetchInvestigationForIncident: legacy endpoint failed:', e && e.message);
  }

  return null;
}

// Status helpers
const _isSetStatus = (v) => v !== undefined && v !== null && String(v).trim() !== '';
const _normalizeStatus = (value) => String(value || '').trim().toUpperCase();

function getInvestigationStatusRank(statusValue) {
  switch (_normalizeStatus(statusValue)) {
    case 'PENDING':
      return 0;
    case 'NEEDS_REVISION':
      return 1;
    case 'SUBMITTED':
      return 2;
    case 'SUPERVISOR_APPROVED':
      return 3;
    case 'COMPLETED':
      return 4;
    default:
      return -1;
  }
}

function isApprovedFlag(value) {
  const normalized = _normalizeStatus(value);
  return normalized === 'APPROVED' || normalized === '1' || normalized === 'TRUE';
}

function isRejectedFlag(value) {
  const normalized = _normalizeStatus(value);
  return normalized === 'REJECTED';
}

function getDerivedInvestigationStatus(record = {}) {
  const explicitStatus = _normalizeStatus(
    record.INVESTIGATION_STATUS ?? record.investigationStatus ?? record.status
  );
  const supervisorApproved = isApprovedFlag(record.SUPERVISOR_APPROVAL) || isApprovedFlag(record.SUPERVISOR_SIGN_OFF);
  const managerApproved = isApprovedFlag(record.MANAGER_APPROVAL) || isApprovedFlag(record.MANAGER_SIGN_OFF);
  const rejected = isRejectedFlag(record.SUPERVISOR_APPROVAL) || isRejectedFlag(record.MANAGER_APPROVAL);

  if (rejected) return 'NEEDS_REVISION';
  if (managerApproved) return 'COMPLETED';
  if (supervisorApproved) return 'SUPERVISOR_APPROVED';
  return explicitStatus;
}

function preferInvestigationStatus(newStatus, prevStatus, fallback = 'PENDING') {
  if (_isSetStatus(newStatus)) return String(newStatus);
  if (_isSetStatus(prevStatus)) return String(prevStatus);
  return fallback;
}

function resolveInvestigationStatus(newData = {}, prev = {}) {
  const newStatus = getDerivedInvestigationStatus(newData || {});
  const prevStatus = getDerivedInvestigationStatus(prev || {});

  if (_normalizeStatus(newStatus) === 'NEEDS_REVISION') {
    return 'NEEDS_REVISION';
  }

  if (!_isSetStatus(newStatus)) {
    return preferInvestigationStatus(prevStatus, null, 'PENDING');
  }

  if (!_isSetStatus(prevStatus)) {
    return preferInvestigationStatus(newStatus, null, 'PENDING');
  }

  return getInvestigationStatusRank(prevStatus) > getInvestigationStatusRank(newStatus)
    ? String(prevStatus)
    : String(newStatus);
}

function getCanonicalIncidentId(incident) {
  if (!incident) return null;
  const candidate = incident.incident_id ?? incident.INCIDENT_ID ?? incident.incidentId ?? incident.ID ?? incident.id ?? null;
  if (candidate === undefined || candidate === null || String(candidate).trim() === '') return null;
  return String(candidate).trim();
}

// client cache helpers (localStorage)
function readClientCache() {
  try {
    const raw = localStorage.getItem(CLIENT_CACHE_KEY);
    if (!raw) return {};
    return JSON.parse(raw);
  } catch (e) {
    console.warn('Failed to read client cache:', e);
    return {};
  }
}

function writeClientCache(obj) {
  try {
    localStorage.setItem(CLIENT_CACHE_KEY, JSON.stringify(obj || {}));
  } catch (e) {
    console.warn('Failed to write client cache:', e);
  }
}

function setClientCacheForId(id, data = {}) {
  if (!id) return;
  const cache = readClientCache();
  cache[String(id)] = { ...(cache[String(id)] || {}), ...(data || {}) };
  writeClientCache(cache);
}

function getClientCacheForId(id) {
  if (!id) return null;
  const cache = readClientCache();
  return cache[String(id)] || null;
}

function clearClientCacheForId(id) {
  if (!id) return;
  const cache = readClientCache();
  delete cache[String(id)];
  writeClientCache(cache);
}

export {
  fetchInvestigationForIncident,
  resolveInvestigationStatus,
  preferInvestigationStatus,
  getCanonicalIncidentId,
  setClientCacheForId,
  getClientCacheForId,
  clearClientCacheForId
};

// Debug helper: expose function to browser console for easy manual invocation
try {
  if (typeof window !== 'undefined') {
    window.__fetchInvestigationForIncident = fetchInvestigationForIncident;
    console.info('[investigationHelpers] debug hook installed: call window.__fetchInvestigationForIncident("301").then(console.log).catch(console.error)');
  }
} catch (e) {
  // ignore
}
